/*
  Mobile colour-swatch slider (Hassan, 2026-09-25): on phones, a product's colour swatches stay on one swipeable row
  when they don't fit, with cues that make the slider obvious — edge fades, the next swatch peeking, round arrow
  buttons, and a one-time nudge when the row first scrolls into view.
  The variant picker re-renders the swatch list's innerHTML + className on every variant change, so all state lives
  on the list's PARENT (class .imp-sw-slider + arrow buttons appended there), never on the list itself.
*/
(() => {
  const MQ = window.matchMedia('(max-width: 749px)');
  const LIST = '.mv-vp-swatches';

  function update(list) {
    const host = list.parentElement;
    if (!host) return;
    const active = MQ.matches && host.classList.contains('imp-sw-slider');
    if (!active) return;
    const max = list.scrollWidth - list.clientWidth;
    host.classList.toggle('imp-sw-slider--left', list.scrollLeft > 4);
    host.classList.toggle('imp-sw-slider--right', list.scrollLeft < max - 4);
    host.style.setProperty('--imp-sw-mid', list.offsetTop + list.offsetHeight / 2 + 'px');
  }

  function arrow(host, list, dir) {
    const b = document.createElement('button');
    b.type = 'button';
    b.className = 'imp-sw-arrow imp-sw-arrow--' + (dir < 0 ? 'prev' : 'next');
    b.setAttribute('aria-label', dir < 0 ? 'Previous colours' : 'More colours');
    b.innerHTML = '<svg viewBox="0 0 24 24" width="12" height="12" aria-hidden="true"><path d="' +
      (dir < 0 ? 'M15 5l-7 7 7 7' : 'M9 5l7 7-7 7') + '" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"/></svg>';
    b.addEventListener('click', () => list.scrollBy({ left: dir * list.clientWidth * 0.7, behavior: 'smooth' }));
    host.appendChild(b);
  }

  function showSelected(list) {
    const checked = list.querySelector('input:checked');
    const item = checked && checked.closest('.mv-vp-swatch');
    if (!item) return;
    const l = item.offsetLeft - list.offsetLeft, r = l + item.offsetWidth;
    if (l < list.scrollLeft || r > list.scrollLeft + list.clientWidth) list.scrollLeft = l - list.clientWidth / 2 + item.offsetWidth / 2;
  }

  function setup(list) {
    const host = list.parentElement;
    if (!host) return;
    if (!MQ.matches) { host.classList.remove('imp-sw-slider', 'imp-sw-slider--left', 'imp-sw-slider--right'); return; }
    // measure with wrapping off: only slide when one row can't hold them all
    host.classList.add('imp-sw-slider');
    if (list.scrollWidth <= list.clientWidth + 2) { host.classList.remove('imp-sw-slider'); return; }
    if (!host.querySelector(':scope > .imp-sw-arrow')) {
      arrow(host, list, -1); arrow(host, list, 1);
      list.addEventListener('scroll', () => update(list), { passive: true });
      new MutationObserver(() => { showSelected(list); update(list); }).observe(list, { childList: true });
      nudge(list);
    }
    showSelected(list);
    update(list);
  }

  function nudge(list) {
    if (!('IntersectionObserver' in window) || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    const io = new IntersectionObserver((entries) => {
      if (!entries[0].isIntersecting) return;
      io.disconnect();
      if (list.scrollLeft > 4) return;
      setTimeout(() => {
        list.scrollTo({ left: 56, behavior: 'smooth' });
        setTimeout(() => list.scrollTo({ left: 0, behavior: 'smooth' }), 650);
      }, 500);
    }, { threshold: 0.9 });
    io.observe(list);
  }

  const run = () => document.querySelectorAll(LIST).forEach(setup);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', run); else run();
  window.addEventListener('load', run);
  MQ.addEventListener('change', run);
  let t; window.addEventListener('resize', () => { clearTimeout(t); t = setTimeout(run, 150); });
  // quick view / drawers render pickers later
  new MutationObserver((muts) => {
    for (const m of muts) for (const n of m.addedNodes) if (n.nodeType === 1 && (n.matches?.(LIST) || n.querySelector?.(LIST))) { run(); return; }
  }).observe(document.body, { childList: true, subtree: true });
})();
